import { FETCH_QUERY_KEYS } from "src/queries/consts";
import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import campaignManagerService from "src/queries/services/campaignManager";
import collectionsService from "src/queries/services/collections";
import { STATICS_SKILLS_KEYS } from "src/components/collections/consts";
import { CACHE_DURATION, SKILLS } from "@consts";

export const useCampaignManager = (params) => useQuery({
  queryKey: [FETCH_QUERY_KEYS.CAMPAIGN_MANAGER, params.accountId, params.assistantId, params.paginationParams],
  queryFn: async () => await campaignManagerService.fetchCampaignManager(params),
  enabled: params.enabled && !!params.accountId && !!params.assistantId,
  staleTime: CACHE_DURATION.ONE_MINUTE,
  placeholderData: keepPreviousData
});

export const useCampaignManagerTasks = (params) => useQuery({
  queryKey: [FETCH_QUERY_KEYS.CAMPAIGN_MANAGER_TASKS, params.accountId, params.campaignId, params.paginationParams],
  queryFn: async () => await campaignManagerService.fetchCampaignTasks(params),
  enabled: params.enabled && !!params.accountId && !!params.campaignId,
  staleTime: CACHE_DURATION.ONE_MINUTE,
  placeholderData: keepPreviousData
});

export const useAllCampaigns = (params) => useQuery({
  queryKey: [FETCH_QUERY_KEYS.ALL_CAMPAIGNS, params.accountId],
  queryFn: async () => await campaignManagerService.fetchAllCampaigns(params),
  enabled: !!params.accountId
});

export const useCampaign = (params) => useQuery({
  queryKey: [FETCH_QUERY_KEYS.CAMPAIGN, params.accountId, params.campaignId],
  queryFn: async () => await campaignManagerService.fetchCampaign(params),
  enabled: !!params.campaignId
});

export const useCreateCampaign = () => useMutation({ mutationFn: campaignManagerService.createCampaign });
export const useDeleteCampaign = () => useMutation({ mutationFn: campaignManagerService.deleteCampaign });
export const useUpdateCampaign = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: campaignManagerService.updateCampaign,
    // The campaign view stays mounted across a save, so nothing else refetches the campaign and
    // every consumer of the cached object keeps reading pre-save values.
    onSuccess: (_data, { accountId, campaignId }) =>
      queryClient.invalidateQueries({ queryKey: [FETCH_QUERY_KEYS.CAMPAIGN, accountId, campaignId] })
  });
};
export const useUpdateCampaignRef = () => useMutation({ mutationFn: campaignManagerService.updateCampaignRef });

const NO_SKILLS = [];

export const useAgentSkills = ({ assistantId }) => {
  const { data, isLoading, isError, isSuccess } = useQuery({
    queryKey: [FETCH_QUERY_KEYS.AGENT_SKILLS, assistantId],
    queryFn: () => collectionsService.fetchCollections({ selectedAssistantId: assistantId, collectionName: SKILLS }),
    enabled: !!assistantId,
    staleTime: CACHE_DURATION.ONE_MINUTE
  });

  return {
    skills: data?.configRef?.filter(({ spec_key, display_key }) => display_key && !STATICS_SKILLS_KEYS.includes(spec_key)) ?? NO_SKILLS,
    isLoading,
    isError,
    isSuccess
  };
};

export const useCampaignDataProcessor = ({ accountId, campaignId, enabled = true }) => useQuery({
  queryKey: [FETCH_QUERY_KEYS.CAMPAIGN_DATA_PROCESSOR, accountId, campaignId],
  queryFn: async () => await campaignManagerService.fetchCampaignDataProcessor({ accountId, campaignId }),
  enabled: enabled && !!accountId && !!campaignId,
  staleTime: CACHE_DURATION.ONE_MINUTE
});

export const useDataProcessorConfig = ({ accountId, dataProcessorId, enabled = true }) => useQuery({
  queryKey: [FETCH_QUERY_KEYS.DATA_PROCESSOR_CONFIG, accountId, dataProcessorId],
  queryFn: async () => await campaignManagerService.fetchDataProcessorConfig({ accountId, dataProcessorId }),
  enabled: enabled && !!accountId && !!dataProcessorId,
  staleTime: CACHE_DURATION.ONE_MINUTE
});

export const useImportCampaignTasks = () => useMutation({ mutationFn: campaignManagerService.importCampaignTasks });

export const useExistingCampaignTasks = () => useMutation({ mutationFn: campaignManagerService.fetchExistingCampaignTasks });

export const useCancelCampaignTasks = () => useMutation({ mutationFn: campaignManagerService.cancelCampaignTasks });

export const useOutboundFilterTypes = (params) => useQuery({
  queryKey: [FETCH_QUERY_KEYS.OUTBOUND_FILTER_TYPES, params.accountId, params.assistantId],
  queryFn: async () => await campaignManagerService.fetchOutboundFilterTypes(params),
  enabled: !!params.accountId && !!params.assistantId,
});
